// src/components/NewUser.jsx
import { Box, Button, TextField, Typography, Alert } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { setToken } from '../utils/auth';

const NewUser = () => {
  const navigate = useNavigate();

  const [form, setForm] = React.useState({
    fullName: '',
    companyName: '',
    email: '',
    password: '',
    repeatPassword: '',
  });
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  async function onSubmit(e) {
    e.preventDefault();
    setError('');

    const email = form.email.trim();

    if (!email || !form.password) {
      setError('Email and password are required.');
      return;
    }
    if (form.password !== form.repeatPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      // 1) Create user (your endpoint returns 201 with no body)
      await axios.post('/auth/users', { email, password: form.password });

      // 2) Immediately log in to get the token
      const res = await axios.post('/auth/token', { email, password: form.password });
      setToken(res.data.access_token);          // <-- important: res.data.access_token

      // 3) Go to the app
      navigate('/', { replace: true });
    } catch (err) {
      // Show FastAPI "detail" if present, else generic message
      setError(err.response?.data?.detail || err.message || 'Could not create account');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box
      sx={{
        mx: 'auto',
        mt: 20,
        display: 'flex',
        flexDirection: 'row',
        borderRadius: 4,
        border: '2px solid #16a4b0',
        overflow: 'hidden',
        width: { xs: '90%', sm: '70%' },
        minHeight: '60vh',
      }}
    >
      {/* Left section (unchanged) */}
      <Box
        sx={{
          flex: 1,
          backgroundColor: '#16a4b0',
          color: 'white',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: 4,
          textAlign: 'center',
        }}
      >
        <Typography variant="h3" fontWeight={700} mb={3}>
          Easy AI for Industry
        </Typography>
        <Typography variant="h6" sx={{ maxWidth: '80%', textAlign: 'center' }}>
          Introducing EARN, the user-friendly application that simplifies AI for industries.
          With its intuitive interface and extensive library of pre-built models, EARN empowers businesses to effortlessly harness the power of AI.
        </Typography>
      </Box>

      {/* Right section (form) */}
      <Box
        component="form"
        onSubmit={onSubmit}
        sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}
      >
        <Typography variant="h4" fontWeight={700} color="black" mb={4}>
          Create New Account
        </Typography>

        <Box sx={{ width: '80%', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Typography variant="h7" color="black" textAlign="left">User Info</Typography>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Full name"
              name="fullName"
              value={form.fullName}
              onChange={onChange}
              fullWidth
              autoComplete="new-full-name"
              sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '12px', '&:hover fieldset': { borderColor: '#00707c' }, '&.Mui-focused fieldset': { borderColor: '#16a4b0' } } }}
            />
            <TextField
              label="Company name"
              name="companyName"
              value={form.companyName}
              onChange={onChange}
              fullWidth
              autoComplete="new-company-name"
              sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '12px', '&:hover fieldset': { borderColor: '#00707c' }, '&.Mui-focused fieldset': { borderColor: '#16a4b0' } } }}
            />
          </Box>

          <Typography variant="h7" color="black" textAlign="left">Email Address</Typography>
          <TextField
            label="Email address"
            name="email"
            value={form.email}
            onChange={onChange}
            fullWidth
            autoComplete="off"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', '&:hover fieldset': { borderColor: '#00707c' }, '&.Mui-focused fieldset': { borderColor: '#16a4b0' } } }}
          />

          <Typography variant="h7" color="black" textAlign="left">Password</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Create your password"
              type="password"
              name="password"
              value={form.password}
              onChange={onChange}
              fullWidth
              autoComplete="off"
              sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '12px', '&:hover fieldset': { borderColor: '#00707c' }, '&.Mui-focused fieldset': { borderColor: '#16a4b0' } } }}
            />
            <TextField
              label="Repeat your password"
              type="password"
              name="repeatPassword"
              value={form.repeatPassword}
              onChange={onChange}
              fullWidth
              autoComplete="off"
              sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '12px', '&:hover fieldset': { borderColor: '#00707c' }, '&.Mui-focused fieldset': { borderColor: '#16a4b0' } } }}
            />
          </Box>
        </Box>

        <Button
          type="submit"
          disabled={loading || !form.email || !form.password || form.password !== form.repeatPassword}
          variant="contained"
          sx={{ mt: 3, borderRadius: '12px', backgroundColor: '#16a4b0', color: 'white', textTransform: 'none', fontSize: '16px', width: '200px', '&:hover': { backgroundColor: '#00707c' } }}
        >
          {loading ? 'Creating…' : 'Create Account'}
        </Button>

        <Button
          variant="text"
          sx={{ textTransform: 'none', color: '#00707c', fontSize: '1rem', mt: 2 }}
          onClick={() => navigate('/login')}
        >
          <u>Already have an account? Sign in here!</u>
        </Button>
      </Box>
    </Box>
  );
};

export default NewUser;
